import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { CheckCheck, Megaphone } from 'lucide-react';
import { getErrorMessage, keys, portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatNumber } from '@blimo/shared';
import { Badge, Button, DocumentList, EmptyState, ErrorState, HtmlFrame, Skeleton, cn, toast } from '@blimo/ui';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemList, ItemRow, Section } from '../components/ui/section.jsx';
import { Pager } from '../components/ui/pager.jsx';

/** Mitteilungen der Verwaltung – read state is kept on the server. */
export function AnnouncementsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const page = search.page ?? 1;
  const query = useQuery(portalQueries.announcements(api, { page }));
  const unread = query.data?.meta?.unread ?? 0;
  const lastPage = Math.max(1, query.data?.meta?.last_page ?? 1);

  async function readAll() {
    try {
      await api.portal.announcements.readAll();
      await queryClient.invalidateQueries({ queryKey: keys.portal.resource('announcements') });
      toast.success('Alle Mitteilungen als gelesen markiert');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }
  const goTo = (next) => navigate({ to: '/mitteilungen', search: { page: next > 1 ? next : undefined } });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        compactTitle="Mitteilungen"
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            Mitteilungen
            {unread > 0 && (
              <Badge variant="info" aria-label={`${unread} ungelesen`}>
                {formatNumber(unread)} ungelesen
              </Badge>
            )}
          </span>
        }
        description="Informationen Ihrer Verwaltung zu Ihren Liegenschaften."
        actions={
          unread > 0 && (
            <Button variant="outline" onClick={readAll}>
              <CheckCheck aria-hidden="true" /> Alle als gelesen markieren
            </Button>
          )
        }
      />
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : query.data.data.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="Keine Mitteilungen"
          description="Sobald Ihre Verwaltung eine Mitteilung für Ihre Liegenschaft veröffentlicht, erscheint sie hier."
        />
      ) : (
        <>
          <ItemList aria-label="Mitteilungen" className={cn(query.isPlaceholderData && 'opacity-60')}>
            {query.data.data.map((item) => (
              <li key={item.id}>
                <ItemRow
                  as={Link}
                  to="/mitteilungen/$announcementId"
                  params={{ announcementId: String(item.id) }}
                  icon={Megaphone}
                  leading={
                    item.has_image ? (
                      <img
                        src={api.portal.announcements.imageUrl(item.id, item.image_version)}
                        alt=""
                        className="size-10 shrink-0 rounded-md border object-cover"
                        loading="lazy"
                      />
                    ) : undefined
                  }
                  title={
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={item.is_read === false ? 'font-semibold' : 'font-normal'}>{item.title}</span>
                      {item.is_read === false && <Badge variant="info">Neu</Badge>}
                    </span>
                  }
                  subtitle={item.teaser ? <span className="line-clamp-2">{item.teaser}</span> : undefined}
                  detail={<span className="numeric text-xs">{formatDate(item.publish_at ?? item.created_at)}</span>}
                />
              </li>
            ))}
          </ItemList>
          <Pager page={page} lastPage={lastPage} onPage={goTo} />
        </>
      )}
    </div>
  );
}

/** Detail – opening it marks the announcement read on the server. */
export function AnnouncementPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { announcementId } = useParams({ strict: false });
  const { data: item, error, isPending, isSuccess, refetch } = useQuery(portalQueries.announcement(api, announcementId));

  // The server marked it read → refresh list + unread badge.
  useEffect(() => {
    if (isSuccess) queryClient.invalidateQueries({ queryKey: [...keys.portal.resource('announcements'), 'list'] });
  }, [isSuccess, queryClient]);

  const back = { to: '/mitteilungen', label: 'Mitteilungen' };
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Mitteilung" back={back} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Mitteilung nicht gefunden" />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Mitteilung" back={back} />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={back}
        title={item.title}
        description={[formatDate(item.publish_at ?? item.created_at), (item.properties ?? []).map((p) => p.name).join(', ')].filter(Boolean).join(' · ')}
      />
      {item.has_image && (
        <img
          src={api.portal.announcements.imageUrl(item.id, item.image_version)}
          alt={`Bild zur Mitteilung ${item.title}`}
          className="max-h-96 w-full max-w-4xl rounded-xl border object-cover"
        />
      )}
      <div className="max-w-4xl overflow-hidden rounded-xl border bg-white">
        <HtmlFrame html={item.body_html ?? ''} title={`Inhalt: ${item.title}`} colorScheme="auto" className="rounded-none border-0" />
      </div>
      {item.attachments?.length > 0 && (
        <Section title="Anhänge" flush className="max-w-4xl">
          <DocumentList
            label="Anhänge"
            documents={item.attachments}
            hrefFor={(doc) => api.portal.announcements.attachmentUrl(item.id, doc.id)}
            className="rounded-none border-0 [&>li]:px-4"
          />
        </Section>
      )}
    </div>
  );
}
